import { Box, CircularProgress, Typography } from '@mui/material'
import React from 'react'

import { Book } from '../../services/api'
import { PAPER_BACKGROUND, PAPER_TEXTURE_OVERLAY } from '../../styles/paperBackground'
import { ContentAST } from '../../transforms/ast'
import { LexicalEditor, type LexicalEditorRef } from '../editor/lexical'

interface SearchPreviewPanelProps {
  sourceFilename?: string
  onOpenEmbedSource?: (filename: string, blockId: string) => void
  previewIdentity?: string | null
  previewTitle?: string | null
  previewAst?: ContentAST | null
  previewLoading?: boolean
  previewError?: string | null
  previewBlockId?: string | null
  previewHighlightText?: string | null
  previewHighlightOffset?: number | null
  book?: Book | null
  onHeaderPointerDown?: React.PointerEventHandler<HTMLDivElement>
  headerDraggable?: boolean
  headerDragging?: boolean
  headerActions?: React.ReactNode
  /** When true the inner editor is editable (toolbar shown, Lexical editable mode). */
  editable?: boolean
  /** Called whenever the edited AST changes (debounced by Lexical). */
  onChange?: (ast: ContentAST) => void
  /** Called when the user invokes save inside the editor (Ctrl+S / toolbar save). */
  onSave?: (ast: ContentAST) => void
  /** Imperative access to the inner editor (e.g. to flush + read the current AST on close). */
  editorRef?: React.Ref<LexicalEditorRef>
  saving?: boolean
  autoSaving?: boolean
  hasUnsavedChanges?: boolean
  recentlySaved?: boolean
}

const SearchPreviewPanel: React.FC<SearchPreviewPanelProps> = ({
  sourceFilename,
  onOpenEmbedSource,
  previewIdentity,
  previewTitle,
  previewAst,
  previewLoading = false,
  previewError,
  previewBlockId,
  previewHighlightText,
  previewHighlightOffset,
  book,
  onHeaderPointerDown,
  headerDraggable = false,
  headerDragging = false,
  headerActions,
  editable = false,
  onChange,
  onSave,
  editorRef,
  saving = false,
  autoSaving = false,
  hasUnsavedChanges = false,
  recentlySaved = false,
}) => {
  return (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: '1px solid var(--border-subtle)',
          bgcolor: 'var(--bg-surface-subtle)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          onPointerDown={onHeaderPointerDown}
          sx={{
            flex: 1,
            minWidth: 0,
            cursor: headerDraggable ? (headerDragging ? 'grabbing' : 'grab') : 'default',
            userSelect: headerDraggable ? 'none' : 'auto',
            touchAction: headerDraggable ? 'none' : 'auto',
          }}
        >
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            {previewTitle || 'Select a result'}
          </Typography>
        </Box>
        {headerActions ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>{headerActions}</Box>
        ) : null}
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', bgcolor: 'var(--bg-app)' }}>
        {previewLoading ? (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
            <CircularProgress size={22} />
            <Typography sx={{ color: 'var(--text-secondary)' }}>Loading preview...</Typography>
          </Box>
        ) : previewError ? (
          <Box sx={{ py: 10, px: 4, textAlign: 'center' }}>
            <Typography sx={{ color: 'error.main', mb: 1 }}>Could not load preview</Typography>
            <Typography sx={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{previewError}</Typography>
          </Box>
        ) : previewAst ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 0, md: 2 } }}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '816px',
                minHeight: '100%',
                background: PAPER_BACKGROUND,
                '&::after': PAPER_TEXTURE_OVERLAY,
                border: { xs: 'none', md: '1px solid var(--toolbar-page-border)' },
              }}
            >
              <LexicalEditor
                key={`${previewIdentity || previewTitle || 'preview'}-${previewBlockId || 'start'}-${previewHighlightOffset || 0}-${editable ? 'edit' : 'read'}`}
                ref={editorRef}
                bookId={book?._id}
                chapterId={sourceFilename}
                onOpenEmbedSource={onOpenEmbedSource}
                initialContent={previewAst}
                readOnly={!editable}
                showToolbar={editable}
                showSaveButton={editable}
                placeholder=""
                isAuthor={editable}
                bookSettings={book?.settings}
                onChange={editable ? onChange : undefined}
                onSave={editable ? onSave : undefined}
                saving={saving}
                autoSaving={autoSaving}
                hasUnsavedChanges={hasUnsavedChanges}
                recentlySaved={recentlySaved}
                initialScrollBlockId={previewBlockId}
                initialScrollHighlightText={previewHighlightText}
                initialScrollHighlightOffset={previewHighlightOffset}
                style={{
                  minHeight: '100%',
                  border: 'none',
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ py: 10, px: 4, textAlign: 'center' }}>
            <Typography sx={{ color: 'var(--text-secondary)' }}>
              Select a result to preview the chapter here.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default SearchPreviewPanel
